import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { useTheme } from '../theme/ThemeContext';
import { createCheckoutSession, createCustomerPortal } from '../utils/api';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

export default function Checkout() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const plan = searchParams.get('plan') || 'pro';
  const { theme } = useTheme();
  const [clientSecret, setClientSecret] = useState(null);
  const [error, setError] = useState(null);
  const [alreadySubscribed, setAlreadySubscribed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setAlreadySubscribed(false);
    setClientSecret(null);
    createCheckoutSession(plan)
      .then((data) => {
        if (cancelled) return;
        if (data.client_secret) {
          setClientSecret(data.client_secret);
        } else {
          setError('Could not start checkout');
        }
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.code === 'EMAIL_NOT_VERIFIED') {
          setError(err.message || 'Please verify your email first to subscribe.');
          return;
        }
        const res = err.response || {};
        const data = res.data || {};
        if (res.status === 400 && data.already_subscribed) {
          setAlreadySubscribed(true);
          setError(data.message || 'You already have an active subscription.');
        } else {
          setError(err.message || 'Checkout failed');
        }
      });
    return () => { cancelled = true; };
  }, [plan]);

  const fetchClientSecret = useCallback(() => {
    if (clientSecret) return Promise.resolve(clientSecret);
    return createCheckoutSession(plan).then((d) => d.client_secret);
  }, [clientSecret, plan]);

  if (alreadySubscribed) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: theme.bg, color: theme.text, padding: 24 }}>
        <div style={{ maxWidth: 480, margin: '0 auto' }}>
          <h2 style={{ marginBottom: 12 }}>Already subscribed</h2>
          <p style={{ color: theme.textMuted, marginBottom: 20 }}>{error}</p>
          <button
            type="button"
            onClick={() => navigate('/profile')}
            style={{
              padding: '12px 20px',
              fontSize: 15,
              fontWeight: 600,
              color: theme.primaryContrast,
              backgroundColor: theme.primary,
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
              marginRight: 12,
            }}
          >
            Back to profile
          </button>
          <button
            type="button"
            onClick={async () => {
              try {
                const r = await createCustomerPortal();
                if (r.portal_url) window.location.href = r.portal_url;
              } catch (e) {
                setError(e.message || 'Could not open billing portal');
              }
            }}
            style={{
              padding: '12px 20px',
              fontSize: 15,
              fontWeight: 600,
              color: theme.text,
              backgroundColor: theme.card,
              border: `1px solid ${theme.border}`,
              borderRadius: 8,
              cursor: 'pointer',
            }}
          >
            Manage subscription
          </button>
        </div>
      </div>
    );
  }

  if (error && !clientSecret) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: theme.bg, color: theme.text, padding: 24 }}>
        <div style={{ maxWidth: 480, margin: '0 auto' }}>
          <h2 style={{ marginBottom: 12 }}>Checkout</h2>
          <p style={{ color: theme.usageError, marginBottom: 20 }}>{error}</p>
          <button
            type="button"
            onClick={() => navigate('/profile')}
            style={{
              padding: '12px 20px',
              fontSize: 15,
              fontWeight: 600,
              color: theme.primaryContrast,
              backgroundColor: theme.primary,
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
            }}
          >
            Back to profile
          </button>
        </div>
      </div>
    );
  }

  if (!clientSecret) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: theme.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 40, height: 40, border: `3px solid ${theme.border}`, borderTopColor: theme.primary, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const isSingleReport = plan === 'single_int_report';

  return (
    <div style={{ minHeight: '100vh', backgroundColor: theme.bg, padding: 24 }}>
      <div style={{ maxWidth: 800, margin: '0 auto' }}>
        <h2 style={{ marginBottom: 8, color: theme.text }}>
          {isSingleReport ? 'Complete your transaction' : 'Complete your subscription'}
        </h2>
        <p style={{ color: theme.textMuted, marginBottom: 24, fontSize: 14 }}>
          {isSingleReport
            ? 'This is a one time purchase, you will not be charged on a recurring basis.'
            : 'You will not be charged again until the end of your billing period. Cancel anytime from your profile.'}
        </p>
        <EmbeddedCheckoutProvider
          stripe={stripePromise}
          options={{ fetchClientSecret }}
        >
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      </div>
    </div>
  );
}
